import { useEffect, useId, useRef, useState } from 'react';

import { useAuth } from '@/app/auth';
import HomeSettingsModal from '@/features/homeAutomation/components/HomeSettingsModal';
import { cn } from '@/lib/cn';

function initialOf(name: string | null | undefined, email: string | null | undefined): string {
  const source = name?.trim() || email?.trim() || '?';
  return source.charAt(0).toUpperCase();
}

export default function UserMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [homeSettingsOpen, setHomeSettingsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const initial = initialOf(user.displayName, user.email);
  const label = user.displayName || user.email || 'Account';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Account menu — signed in as ${label}`}
        className={cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-full border border-transparent bg-(--bg-surface) text-sm font-semibold text-(--text-primary) transition-colors',
          'hover:border-(--border-default) hover:bg-(--bg-surface-hover)',
          open && 'border-(--border-default) bg-(--bg-surface-strong)',
        )}
      >
        {user.photoURL ? (
          <img src={user.photoURL} alt="" className="h-full w-full rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span aria-hidden>{initial}</span>
        )}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Account"
          className={cn(
            'absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border shadow-(--shadow-modal)',
            'border-(--border-default) bg-(--bg-surface-strong) backdrop-blur',
          )}
        >
          <div className="border-b border-(--border-subtle) px-4 py-3">
            <p className="truncate text-sm font-medium text-(--text-primary)">{user.displayName ?? 'Signed in'}</p>
            {user.email && <p className="truncate text-xs text-(--text-tertiary)">{user.email}</p>}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setHomeSettingsOpen(true);
            }}
            className="block w-full px-4 py-2.5 text-left text-sm text-(--text-primary) transition-colors hover:bg-(--bg-surface-hover)"
          >
            Home settings
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className="block w-full px-4 py-2.5 text-left text-sm text-(--text-primary) transition-colors hover:bg-(--bg-surface-hover)"
          >
            Sign out
          </button>
        </div>
      )}
      <HomeSettingsModal open={homeSettingsOpen} onClose={() => setHomeSettingsOpen(false)} />
    </div>
  );
}
